<template>
    <div class="pb-5 sm:pb-0">
        <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
            <h1
                class="flex items-center space-x-2 text-2xl font-semibold text-taupe-900 tracking-tight"
            >
                <span>
                    {{ flag.name }}
                </span>
                <span
                    v-if="flag.permanent"
                    data-testid="flag-permanent-icon"
                    class="text-taupe-500"
                >
                    <zen-tooltip
                        content="This flag is permanent and will not show up in the Stale Flags report."
                        placement="right"
                        v-slot="{ setReference, ariaAttrs, show, hide }"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="2"
                            stroke="currentColor"
                            class="w-5 h-5"
                            tabindex="0"
                            :ref="setReference"
                            v-bind="ariaAttrs"
                            @mouseenter="show"
                            @mouseleave="hide"
                            @focus="show"
                            @blur="hide"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                            />
                        </svg>
                    </zen-tooltip>
                </span>
            </h1>
            <h2 class="text-sm">
                <zen-tooltip
                    content="Click to copy"
                    placement="right"
                    v-slot="{ setReference, ariaAttrs, show, hide }"
                >
                    <button
                        type="button"
                        data-testid="flag-key"
                        :ref="setReference"
                        v-bind="ariaAttrs"
                        class="cursor-pointer rounded-md border border-taupe-200 bg-white px-2 py-0.5 font-mono text-xs text-taupe-600 shadow-xs transition-colors hover:border-sky-300 hover:text-sky-600"
                        @mouseenter="show"
                        @mouseleave="hide"
                        @focus="show"
                        @blur="hide"
                        v-clipboard:copy="flag.key"
                        v-clipboard:success="onCopy"
                        v-clipboard:error="onCopyError"
                    >
                        {{ flag.key }}
                    </button>
                </zen-tooltip>
            </h2>
        </div>
        <p
            v-if="flag.description"
            data-testid="flag-description"
            class="mt-3 max-w-3xl text-pretty break-words text-sm leading-6 text-taupe-600"
        >
            {{ flag.description }}
        </p>
        <div class="mt-3 sm:mt-4">
            <!-- Dropdown menu on small screens -->
            <div class="sm:hidden">
                <label for="current-tab" class="sr-only">Select a tab</label>
                <select
                    id="current-tab"
                    name="current-tab"
                    :value="current"
                    @change="toggle($event.target.value)"
                    class="block w-full rounded-md border-taupe-300 py-2 pl-3 pr-10 text-base focus:border-sky-500 focus:outline-hidden focus:ring-sky-500 sm:text-sm"
                >
                    <option
                        v-for="tabOption in mobileTabOptions"
                        :key="tabOption.value"
                        :value="tabOption.value"
                    >
                        {{ tabOption.label }}
                    </option>
                </select>
            </div>
            <!-- Tabs at small breakpoint and up -->
            <div class="hidden sm:block mb-2">
                <zen-tab-list>
                    <zen-tab :active="isTargets" @click="toggle('target')"
                        >Target</zen-tab
                    >
                    <zen-tab :active="isUsage" @click="toggle('usage')"
                        >Usage</zen-tab
                    >
                    <zen-tab :active="isValues" @click="toggle('values')"
                        >Values</zen-tab
                    >
                    <zen-tab :active="isHistory" @click="toggle('history')"
                        >History</zen-tab
                    >
                    <zen-tab
                        v-if="canManageFlag"
                        :active="isSettings"
                        @click="toggle('settings')"
                        >Settings</zen-tab
                    >
                </zen-tab-list>
            </div>
        </div>
    </div>
</template>

<script>
import { errorMessage, infoMessage } from '@/utils/notifications'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'

export default {
    name: 'FlagHeaderTabs',
    setup() {
        const { can } = usePermissions()
        return { can, PERMISSIONS }
    },
    computed: {
        permissionScope() {
            return {
                projectUlid: this.projectUlid,
                environmentUlid: this.environmentUlid,
            }
        },
        canManageFlag() {
            return (
                this.can(PERMISSIONS.FLAG_UPDATE, this.permissionScope) ||
                this.can(PERMISSIONS.FLAG_DELETE, this.permissionScope)
            )
        },
        isHistory() {
            return this.current === 'history'
        },
        isSettings() {
            return this.current === 'settings'
        },
        isTargets() {
            return this.current === 'target'
        },
        isUsage() {
            return this.current === 'usage'
        },
        isValues() {
            return this.current === 'values'
        },
        mobileTabOptions() {
            const options = [
                { value: 'target', label: 'Target' },
                { value: 'usage', label: 'Usage' },
                { value: 'values', label: 'Values' },
                { value: 'history', label: 'History' },
            ]
            if (this.canManageFlag) {
                options.push({ value: 'settings', label: 'Settings' })
            }
            return options
        },
    },
    methods: {
        onCopy(_e) {
            infoMessage('Flag key has been copied to your clipboard.')
        },
        onCopyError(_e) {
            errorMessage('Failed to copy flag key to your clipboard.')
        },
        toggle(value) {
            this.$emit('changed', value)
        },
    },
    props: ['current', 'flag', 'projectUlid', 'environmentUlid'],
}
</script>
